import { apiClient } from './apiClient';

export interface UserRank {
  id: string;
  faction: string;
  level: number;
  name: string;
  pointsRequired: number;
  iconUrl?: string;
}

export interface ClanRank {
  id: string;
  level: number;
  name: string;
  pointsRequired: number;
  iconUrl?: string;
}

export interface RefereeRank {
  id: string;
  level: number;
  name: string;
  pointsRequired: number;
  iconUrl?: string;
}

export interface CreateUserRankRequest {
  faction: string;
  level: number;
  name: string;
  pointsRequired: number;
  iconUrl?: string;
}

export interface UpdateUserRankRequest {
  faction: string;
  level: number;
  name: string;
  pointsRequired: number;
  iconUrl?: string;
}

export interface CreateClanRankRequest {
  level: number;
  name: string;
  pointsRequired: number;
  iconUrl?: string;
}

export interface UpdateClanRankRequest {
  level: number;
  name: string;
  pointsRequired: number;
  iconUrl?: string;
}

export interface CreateRefereeRankRequest {
  level: number;
  name: string;
  pointsRequired: number;
  iconUrl?: string;
}

export interface UpdateRefereeRankRequest {
  level: number;
  name: string;
  pointsRequired: number;
  iconUrl?: string;
}

class AdminRanksService {
  // USER RANKS
  async getUserRanks(): Promise<UserRank[]> {
    return await apiClient.get<UserRank[]>('/api/admin/ranks/user');
  }

  async createUserRank(request: CreateUserRankRequest, imageFile?: File): Promise<UserRank> {
    const formData = new FormData();
    formData.append('faction', request.faction);
    formData.append('level', request.level.toString());
    formData.append('name', request.name);
    formData.append('pointsRequired', request.pointsRequired.toString());
    if (request.iconUrl) formData.append('iconUrl', request.iconUrl);
    if (imageFile) formData.append('iconFile', imageFile);

    return await apiClient.postFormData<UserRank>('/api/admin/ranks/user', formData);
  }

  async updateUserRank(id: string, request: UpdateUserRankRequest, imageFile?: File): Promise<UserRank> {
    const formData = new FormData();
    formData.append('faction', request.faction);
    formData.append('level', request.level.toString());
    formData.append('name', request.name);
    formData.append('pointsRequired', request.pointsRequired.toString());
    if (request.iconUrl) formData.append('iconUrl', request.iconUrl);
    if (imageFile) formData.append('iconFile', imageFile);

    return await apiClient.putFormData<UserRank>(`/api/admin/ranks/user/${id}`, formData);
  }

  async deleteUserRank(id: string): Promise<void> {
    return await apiClient.delete(`/api/admin/ranks/user/${id}`);
  }

  // CLAN RANKS
  async getClanRanks(): Promise<ClanRank[]> {
    return await apiClient.get<ClanRank[]>('/api/admin/ranks/clan');
  }

  async createClanRank(request: CreateClanRankRequest, imageFile?: File): Promise<ClanRank> {
    const formData = new FormData();
    formData.append('level', request.level.toString());
    formData.append('name', request.name);
    formData.append('pointsRequired', request.pointsRequired.toString());
    if (request.iconUrl) formData.append('iconUrl', request.iconUrl);
    if (imageFile) formData.append('iconFile', imageFile);

    return await apiClient.postFormData<ClanRank>('/api/admin/ranks/clan', formData);
  }

  async updateClanRank(id: string, request: UpdateClanRankRequest, imageFile?: File): Promise<ClanRank> {
    const formData = new FormData();
    formData.append('level', request.level.toString());
    formData.append('name', request.name);
    formData.append('pointsRequired', request.pointsRequired.toString());
    if (request.iconUrl) formData.append('iconUrl', request.iconUrl);
    if (imageFile) formData.append('iconFile', imageFile);

    return await apiClient.putFormData<ClanRank>(`/api/admin/ranks/clan/${id}`, formData);
  }

  async deleteClanRank(id: string): Promise<void> {
    return await apiClient.delete(`/api/admin/ranks/clan/${id}`);
  }

  // REFEREE RANKS
  async getRefereeRanks(): Promise<RefereeRank[]> {
    return await apiClient.get<RefereeRank[]>('/api/admin/ranks/referee');
  }

  async createRefereeRank(request: CreateRefereeRankRequest, imageFile?: File): Promise<RefereeRank> {
    const formData = new FormData();
    formData.append('level', request.level.toString());
    formData.append('name', request.name);
    formData.append('pointsRequired', request.pointsRequired.toString());
    if (request.iconUrl) formData.append('iconUrl', request.iconUrl);
    if (imageFile) formData.append('iconFile', imageFile);

    return await apiClient.postFormData<RefereeRank>('/api/admin/ranks/referee', formData);
  }

  async updateRefereeRank(id: string, request: UpdateRefereeRankRequest, imageFile?: File): Promise<RefereeRank> {
    const formData = new FormData();
    formData.append('level', request.level.toString());
    formData.append('name', request.name);
    formData.append('pointsRequired', request.pointsRequired.toString());
    if (request.iconUrl) formData.append('iconUrl', request.iconUrl);
    if (imageFile) formData.append('iconFile', imageFile);

    return await apiClient.putFormData<RefereeRank>(`/api/admin/ranks/referee/${id}`, formData);
  }

  async deleteRefereeRank(id: string): Promise<void> {
    return await apiClient.delete(`/api/admin/ranks/referee/${id}`);
  }

}

export default new AdminRanksService();